import type { PositionPriceAdjustment } from '../../enums'

import { CATALOGUE } from './catalogue'
import { COMMON } from './common'
import { COST_TERMS, positionsInProjects } from './costs'
import { POSITION_PRICE_ADJUSTMENT_LABELS } from './enum-labels'
import { TERMS } from './terms'

/** What an adjustment does to the quoted figure, and what the number beside it
 *  is. A `Record` each, so a fourth kind in `schema.prisma` fails the build
 *  until it has both. Its *name* is in `enum-labels.ts`, where every enum's is. */
type AdjustmentLabels = Record<PositionPriceAdjustment, string>

// Labels for the entry's own fields are NOT here — a CostEntry's name,
// flatRateCost, quantity and description resolve through `useField(key)`,
// the same source the planning table heads its columns with.
export const COST_ENTRY = {
  add: `${COST_TERMS.position} hinzufügen`,
  edit: `${COST_TERMS.positions} bearbeiten`,
  empty: `Noch keine ${COST_TERMS.positions} erfasst`,
  searchPlaceholder: `${COST_TERMS.position} suchen …`,
  visibleToCustomer: 'Dem Kunden in der Aufstellung zeigen',
  delete: {
    confirmTitle: `${COST_TERMS.position} löschen`,
    confirmMessage: 'Diese Position wirklich löschen?',
    /** Refused for a position a pick wrote: the configurator owns the row, and
     *  the next save would put it back at today's catalogue price. */
    isPick:
      'Diese Position gehört zur Auswahl des Kunden. Sie verschwindet, wenn er die Maßnahme abwählt — gelöscht legt der nächste Speichervorgang sie zum heutigen Katalogpreis neu an.',
  },

  /** The measures at the same place a measure rules out, named in the picker
   *  that refuses it and on the position that sits beside them anyway. */
  excludedBy: (titles: readonly string[]) =>
    `${CATALOGUE.measure.exclusions}: ${titles.join(', ')}`,
  /** The server refusing a measure for the same reason. */
  excludedByRefused: (title: string, titles: readonly string[]) =>
    `„${title}“ – ${COST_ENTRY.excludedBy(titles)}`,

  /** Adding a blocked measure anyway, which only an administrator may. */
  override: {
    /** Beside the submit button, where little room is left. */
    warning: (n: number) => `${n} gesperrt`,
    confirmTitle: 'Gesperrte Maßnahmen übergehen?',
    confirmMessage: (titles: readonly string[]) =>
      titles.length === 1
        ? `„${titles[0]}“ ist hier gesperrt oder schließt sich mit einer vorhandenen Position aus. Sie wird trotzdem angelegt und in der Liste gekennzeichnet.`
        : `${titles.map(title => `„${title}“`).join(', ')} sind hier gesperrt oder schließen sich mit vorhandenen Positionen aus. Sie werden trotzdem angelegt und in der Liste gekennzeichnet.`,
    confirm: 'Trotzdem hinzufügen',
    adminOnly: 'Gesperrte Maßnahmen übergehen nur Administratoren.',
  },

  /** The dialog that picks measures out of the catalogue. */
  picker: {
    title: `${COST_TERMS.positions} aus dem Katalog`,
    showBlocked: 'Gesperrte zeigen',
    /** The badge on a row another measure at this place rules out. */
    excluded: 'Ausgeschlossen',
    required: 'Zwangsauswahl',
    requiredHint: 'Diese Maßnahmen fordert das Objekt.',
    freeInstead: 'Stattdessen freie Position anlegen',
    /** Beside the submit button: ticked measures something now blocks, which
     *  are left out. */
    dropped: (n: number) =>
      n === 1 ? '1 wird nicht hinzugefügt' : `${n} werden nicht hinzugefügt`,
    // Two forms rather than a slot: the noun is inflected either way and the
    // count sits in front of it.
    submit: (n: number) =>
      n === 1 ? '1 Position hinzufügen' : `${n} Positionen hinzufügen`,
  },

  /** The pane that opens beside the positions list. */
  pane: {
    details: 'Angaben',
    origin: 'Herkunft',
    /** Who put the position on the plan — a person, where `createdAt` is a
     *  time, so the two are separate rows. */
    createdBy: 'Erstellt von',
    freeText: 'Frei angelegt',
    openMeasure: `${TERMS.measure} im Katalog öffnen`,
    /** Which of the catalogue's gates somebody walked past to add this, and
     *  the expression as it read at the time. */
    overridden: 'Übergangene Sperre',
    overriddenRule: 'Regel beim Anlegen',
  },

  /**
   * What a position costs, and what an administrator may do about it.
   *
   * One figure on screen: what the customer is charged. What it was before an
   * adjustment, when it was quoted and who moved it ride along as the row's own
   * sentence — a line each would make four rows read like four prices. The
   * price row itself is the `flatRateCost` field and takes its label from
   * `FIELD_LABELS`.
   */
  price: {
    group: 'Preis',
    /** Who moved the price off what the catalogue said. The kind and the
     *  figure are not repeated here — the figure is the price two rows up, and
     *  the struck-through one beside it says it was moved. */
    adjustedBy: 'Angepasst von',

    /** A percentage as the pane prints it. */
    percent: (value: string) => `${value} %`,

    /** The second row, shown only where the catalogue no longer agrees. */
    live: 'Aktueller Preis',
    liveHint: `Was dieselbe Auswahl heute kosten würde, neu gerechnet aus Katalog und Objektdaten. Der Kunde zahlt weiter den angebotenen Preis — bis du die Position auf den aktuellen Preis bringst.`,

    /** Said where the two figures are not both on screen: the list's tooltip. */
    moved: (quoted: string, live: string) =>
      `Angeboten für ${quoted}, laut Katalog heute ${live}. Der Kunde behält den angebotenen Preis, bis du die Position auf den aktuellen Preis bringst.`,
    /** Said in the pane, where the figures stand in the rows underneath. */
    movedHere:
      'Der Katalog rechnet diese Position inzwischen anders. Der Kunde behält den angebotenen Preis.',

    adjust: 'Preis anpassen',
    refresh: 'Auf aktuellen Preis bringen',
    refreshHint:
      'Die Position wird neu bepreist, als hätte der Kunde sie gerade ausgewählt. Eine bestehende Anpassung entfällt dabei.',
    kind: POSITION_PRICE_ADJUSTMENT_LABELS,
    valueLabel: {
      FIXED: COMMON.amount,
      DISCOUNT_PERCENT: 'Rabatt',
      DISCOUNT_AMOUNT: 'Rabatt',
    } satisfies AdjustmentLabels,
    /** The option that charges what the catalogue says, i.e. no adjustment. */
    none: 'Keine Anpassung',
    errors: {
      incomplete: 'Gib an, wie der Preis angepasst wird, und mit welchem Wert.',
      discountRange: 'Ein Rabatt liegt zwischen 0 und 100 Prozent.',
      noLivePrice:
        'Zu dieser Position gibt es keinen Katalogpreis, gegen den sie neu bepreist werden könnte.',
      ownLine:
        'Diese Position hat keinen Katalogpreis, von dem sie abweichen könnte — ihren Betrag trägst du direkt ein.',
      negative: 'Ein Preis ist nicht negativ. Eine Minderung ist ein Rabatt.',
      pickIsOwned:
        'Diese Position gehört zur Auswahl des Kunden. Ihren Preis änderst du über „Preis anpassen“, und sichtbar ist sie, weil der Kunde sie gewählt hat.',
    },
  },

  /**
   * What the advisor tells this customer about one position — the one text on
   * a position that is always theirs, wherever a measure describes the rest.
   * Its label is the field's, read through `useField('customerHint')`.
   */
  customerHint: {
    /** Said beside the field, where the eye says the customer reads it. */
    visibleToCustomer: 'Dieser Hinweis steht beim Kunden an der Position.',
    placeholder: 'Was der Kunde zu dieser Position wissen soll',
  },

  /**
   * What the offer states a position as, frozen when it was quoted — and what
   * the catalogue says about it today, where the two have parted.
   *
   * Its own block and not part of `price`, because a reworded measure is not a
   * repricing: nothing is held, no total moves, and the figure on the row is
   * not what changed.
   */
  serviceDescription: {
    /** The second row, shown only where the measure no longer agrees. */
    live: 'Aktueller Text',
    liveHint:
      'Wie die Maßnahme diese Leistung heute beschreibt. Das Angebot nennt weiter den Text von damals — bis du ihn übernimmst.',

    /** Said where both texts are not on screen: the list's tooltip. */
    moved:
      'Die Maßnahme beschreibt diese Leistung inzwischen anders. Das Angebot nennt weiter den Text, mit dem es verkauft wurde.',

    retake: 'Aktuellen Text übernehmen',
    retakeHint:
      'Die Position nennt danach den Text, den die Maßnahme heute trägt. Preis und Freigabe bleiben, wie sie sind.',

    errors: {
      unchanged:
        'Die Maßnahme beschreibt diese Leistung genau so, wie die Position es schon tut.',
      fromCatalogue:
        'Diesen Text trägt die Position aus der Maßnahme — so, wie er beim Angebot galt. Du kannst den aktuellen Text übernehmen, aber keinen eigenen eintragen.',
    },
  },

  /**
   * A position that no longer answers a question, because somebody changed
   * the catalogue or the configurator's structure out from under it.
   *
   * It keeps the figure it was quoted at and counts in the base renovation;
   * what it loses is the customer, who can neither see it nor change it. The
   * consequence is the same whichever change caused it, so it is worded once
   * here and the reason rides along as a label.
   */
  orphan: {
    label: 'Verwaist',
    /** The glyph in the list. */
    mark: (reason: string) => `Verwaist: ${reason}`,
    /** Heads the confirmation an administrator has to give. */
    confirmTitle: `Angebotene ${COST_TERMS.positions} verlieren ihre Zuordnung`,
    /** States the aggregate. The list under it names each one. */
    confirmMessage: (positions: number, projects: number) =>
      `${positionsInProjects(positions, projects)} beantwortet danach keine Frage mehr.`,
    /** Said once under the message, because it holds for every one of them. */
    confirmConsequence:
      'Diese Positionen bleiben zum angebotenen Preis auf der Planung, zählen zur Basissanierung und werden rot markiert. Der Kunde sieht sie nicht mehr und kann sie nicht mehr ändern.',
    confirmButton: 'Trotzdem ändern',
    /** Beside a position in that list, where the figure was decided on. */
    isLocked: 'Gesperrt',
    isAdjusted: 'Preis angepasst',
    /** The system comment the position carries afterwards. */
    comment: (reason: string) =>
      `Verwaist — diese Position beantwortet keine Frage mehr: ${reason}`,
  },

  /** A person and a moment as one value — who adjusted a price, who locked a
   *  position. Named after neither, because both rows render it. */
  decidedAt: (who: string, at: string) => `${who}, ${at}`,

  /**
   * A position the customer may not change.
   *
   * Follows from the price rather than being switched on: a position charging
   * something other than what the catalogue would charge for the same pick is
   * locked, until an administrator releases it.
   */
  lock: {
    label: 'Auswahl gesperrt',
    /** Heads the column beside `Auswahl`, where the full label would read as a
     *  second kind of selection. */
    column: 'Gesperrt',
    lockedHint:
      'Eine gesperrte Position kann der Kunde weder abwählen noch ein anderes Produkt dafür wählen. Von sich aus gesperrt ist, was vom aktuellen Katalogpreis abweicht.',
    unlock: 'Freigeben',
    lock: 'Sperren',
    /** Who decided, by what they decided. The moment rides along as one value,
     *  the way the price's own row states it. */
    lockedBy: 'Gesperrt von',
    releasedBy: 'Freigegeben von',
    /** Beside the person who decided: hands the position back to the price,
     *  which is the state it was in before anybody pressed anything. */
    reset: 'Zurücksetzen',
    resetHint:
      'Die Entscheidung aufheben. Danach gilt wieder die Regel: gesperrt ist, was vom aktuellen Katalogpreis abweicht.',
    /** The glyph in the list, which has no room for a row. Only the lock: an
     *  open one on every other pick is a texture, not a distinction. */
    markLocked: 'Für den Kunden gesperrt',
    /** Said in the dialog that moves a price: what it does beyond the figure. */
    adjustNote:
      'Weicht der Preis vom aktuellen Katalogpreis ab, ist die Position für den Kunden gesperrt. Du kannst sie danach wieder freigeben.',
  },

  /** What a position carries that is worth a mark in the list. */
  mark: {
    free: 'Frei angelegt, ohne Maßnahme aus dem Katalog',
    overridden: (rule: string) => `Regel übergangen: ${rule}`,
    overriddenNotAvailable:
      'Übergangen: Die Maßnahme ist im Katalog nicht als Position freigegeben',
    stale: (rule: string) => `Passt nicht mehr zu den Objektdaten: ${rule}`,
    staleNotAvailable:
      'Die Maßnahme ist im Katalog nicht mehr als Position freigegeben',
  },
} as const
